import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { memo, type ReactNode } from 'react';
import { StyleSheet, type ViewProps } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { MINIMUM_SPACING_BETWEEN_TARGETS } from '@/util/accessibleTouchTarget';

const CHAT_TOOLBAR_MIN_HEIGHT = isWeb ? 20 : 24;

type ChatToolbarProps = {
  children: ReactNode;
} & ViewProps;

/**
 * Action row below the chat composer. Renders attachment, reference, link, and send buttons.
 * Touch targets and spacing follow util/accessibleTouchTarget (WCAG).
 * On web, the icon buttons are 40px touch targets wrapping 20px glyphs, so
 * negative `marginTop`/`marginBottom` crop the buttons' internal slack and
 * keep the row visually tight against the text above and the border below.
 */
export const ChatToolbar = memo(function ChatToolbar({
  children,
  className,
  style,
  ...props
}: ChatToolbarProps) {
  return (
    <HStack
      className={twJoin('w-full items-center py-0', isWeb ? 'px-px' : 'px-1', className)}
      style={StyleSheet.flatten([
        {
          minHeight: CHAT_TOOLBAR_MIN_HEIGHT,
          gap: MINIMUM_SPACING_BETWEEN_TARGETS,
          paddingBottom: 0,
          ...(isWeb ? { marginTop: -12, marginBottom: -4 } : { marginBottom: -2 }),
        },
        style,
      ])}
      {...props}>
      {children}
    </HStack>
  );
});

ChatToolbar.displayName = 'ChatToolbar';
